'use client';

import posthog from 'posthog-js';
import { useEffect } from 'react';

import { hasAnalyticsOptOutFromDocument } from '@/lib/analytics/opt-out';
import {
  ANALYTICS_CONSENT_CHANGE_EVENT,
  getUsercentricsAnalyticsConsent,
  hasUsercentricsInteraction,
  type UsercentricsEventDetail,
} from '@/lib/analytics/usercentrics';

async function applyUsercentricsConsent(detail?: UsercentricsEventDetail) {
  if (hasAnalyticsOptOutFromDocument()) {
    return;
  }

  if (!hasUsercentricsInteraction(detail)) {
    return;
  }

  const granted = await getUsercentricsAnalyticsConsent(detail);

  if (granted === null) {
    return;
  }

  const nextStatus = granted ? 'granted' : 'denied';

  if (posthog.get_explicit_consent_status() !== nextStatus) {
    if (granted) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  }

  window.dispatchEvent(
    new CustomEvent(ANALYTICS_CONSENT_CHANGE_EVENT, {
      detail: { status: nextStatus },
    })
  );
}

export default function ConsentBridge() {
  useEffect(() => {
    void applyUsercentricsConsent();

    const handleConsent = (event: Event) => {
      void applyUsercentricsConsent(event instanceof CustomEvent ? event.detail : undefined);
    };

    window.addEventListener('UC_CONSENT', handleConsent);
    window.addEventListener('ucEvent', handleConsent);
    window.addEventListener('UC_UI_INITIALIZED', handleConsent);

    return () => {
      window.removeEventListener('UC_CONSENT', handleConsent);
      window.removeEventListener('ucEvent', handleConsent);
      window.removeEventListener('UC_UI_INITIALIZED', handleConsent);
    };
  }, []);

  return null;
}
